import firebase from '../Firebase/Firebase';
import { firebaseVapidKey } from '../Config/env';

const SW_URL = '/firebase-messaging-sw.js?v=140';
let controllerReloadRegistered = false;

export const isIOSDevice = () => {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

export const isAndroidDevice = () => {
  if (typeof navigator === 'undefined') return false;
  return /Android/i.test(navigator.userAgent || '');
};

export const isSafariBrowser = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const appleWebKit = /AppleWebKit/i.test(ua);
  const otherIosBrowser = /(CriOS|FxiOS|EdgiOS|OPiOS|DuckDuckGo)/i.test(ua);
  return appleWebKit && !otherIosBrowser;
};

export const isStandaloneApp = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(display-mode: standalone)').matches
    || window.navigator.standalone === true;
};

export const getPushCapability = () => {
  if (typeof window === 'undefined') {
    return { supported: false, reason: 'server', ios: false, standalone: false };
  }

  const ios = isIOSDevice();
  const standalone = isStandaloneApp();

  if (ios && !standalone) {
    return {
      supported: false,
      reason: 'ios-install-required',
      ios,
      standalone,
      message: 'Sur iPhone et iPad, installez d’abord Le Petit Boulot sur l’écran d’accueil.',
    };
  }

  if (!window.isSecureContext) {
    return {
      supported: false,
      reason: 'https-required',
      ios,
      standalone,
      message: 'Les notifications nécessitent une connexion HTTPS sécurisée.',
    };
  }

  const browserApisAvailable = (
    'Notification' in window
    && 'serviceWorker' in navigator
    && 'PushManager' in window
  );

  if (!browserApisAvailable || firebase.messaging.isSupported?.() === false) {
    return {
      supported: false,
      reason: 'unsupported-browser',
      ios,
      standalone,
      message: 'Ce navigateur ne fournit pas toutes les fonctions nécessaires aux notifications.',
    };
  }

  return {
    supported: true,
    reason: '',
    ios,
    standalone,
    permission: Notification.permission,
  };
};

const tokenDocumentId = async (token) => {
  if (typeof crypto !== 'undefined' && crypto.subtle && typeof TextEncoder !== 'undefined') {
    const bytes = new TextEncoder().encode(token);
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest))
      .map((value) => value.toString(16).padStart(2, '0'))
      .join('');
  }

  return token.replace(/[^a-zA-Z0-9]/g, '').slice(0, 120);
};

const registerAutomaticReload = () => {
  if (controllerReloadRegistered || typeof navigator === 'undefined') return;
  controllerReloadRegistered = true;

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (window.sessionStorage.getItem('lpb-sw-reloaded') === '1') return;
    window.sessionStorage.setItem('lpb-sw-reloaded', '1');
    window.location.reload();
  });

  window.addEventListener('load', () => {
    window.sessionStorage.removeItem('lpb-sw-reloaded');
  }, { once: true });
};

export const registerAppServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) return null;
  registerAutomaticReload();

  const registration = await navigator.serviceWorker.register(SW_URL, {
    scope: '/',
    updateViaCache: 'none',
  });

  registration.addEventListener('updatefound', () => {
    const worker = registration.installing;
    if (!worker) return;
    worker.addEventListener('statechange', () => {
      if (worker.state === 'installed' && navigator.serviceWorker.controller) {
        worker.postMessage?.({ type: 'SKIP_WAITING' });
      }
    });
  });

  await registration.update().catch(() => undefined);
  await navigator.serviceWorker.ready;
  return registration;
};

const friendlyMessagingError = (error) => {
  const code = error?.code || '';

  if (code.includes('permission-blocked')) {
    return new Error('Les notifications sont bloquées dans les réglages de ce navigateur.');
  }
  if (code.includes('unsupported-browser')) {
    return new Error('Ce navigateur ne prend pas en charge les notifications dans ce mode.');
  }
  if (code.includes('token-subscribe-failed')) {
    return new Error('Le service de notification n’a pas pu enregistrer cet appareil. Réessayez après avoir rechargé la page.');
  }
  if (code.includes('failed-service-worker-registration')) {
    return new Error('Le service de notification n’a pas pu démarrer sur cet appareil.');
  }

  return error instanceof Error ? error : new Error('Impossible d’activer les notifications.');
};

export const enablePushNotifications = async (
  userId,
  { requestPermission = true } = {},
) => {
  if (!userId) throw new Error('Utilisateur non connecté.');
  if (!firebaseVapidKey) throw new Error('La clé de notification publique VAPID n’est pas configurée.');

  const capability = getPushCapability();
  if (!capability.supported) {
    const error = new Error(capability.message || 'Notifications non prises en charge.');
    error.reason = capability.reason;
    throw error;
  }

  let permission = Notification.permission;
  if (permission === 'default' && requestPermission) {
    permission = await Notification.requestPermission();
  }

  if (permission === 'default') {
    throw new Error('Touchez « Activer les notifications » pour autoriser cet appareil.');
  }
  if (permission !== 'granted') {
    const error = new Error('Autorisation de notification refusée dans les réglages du navigateur.');
    error.reason = 'permission-denied';
    throw error;
  }

  try {
    const registration = await registerAppServiceWorker();
    const messaging = firebase.messaging();
    const token = await messaging.getToken({
      vapidKey: firebaseVapidKey,
      serviceWorkerRegistration: registration,
    });

    if (!token) throw new Error('Impossible de créer le jeton de notification.');

    const id = await tokenDocumentId(token);
    await firebase.firestore()
      .collection('users')
      .doc(userId)
      .collection('push_tokens')
      .doc(id)
      .set({
        token,
        platform: navigator.platform || '',
        user_agent: navigator.userAgent || '',
        enabled: true,
        standalone: capability.standalone,
        ios: capability.ios,
        updated_at: firebase.firestore.FieldValue.serverTimestamp(),
        created_at: firebase.firestore.FieldValue.serverTimestamp(),
      }, { merge: true });

    return token;
  } catch (error) {
    throw friendlyMessagingError(error);
  }
};

export const listenForForegroundMessages = (onMessage) => {
  try {
    if (!firebase.messaging.isSupported?.()) return () => {};
    const messaging = firebase.messaging();
    return messaging.onMessage((payload) => onMessage?.(payload));
  } catch (_) {
    return () => {};
  }
};
